import type { CheckoutPayerInput } from '@checkout/core'

export interface AirwallexBillingPayload {
  email?: string
  first_name?: string
  last_name?: string
  phone_number?: string
  address?: {
    city?: string
    country_code?: string
    postcode?: string
    state?: string
    street?: string
  }
}

function normalizeField(value: string | undefined): string | undefined {
  const normalized = value?.trim()
  return normalized || undefined
}

function normalizeCountry(value: string | undefined): string | undefined {
  return normalizeField(value)?.toUpperCase()
}

function splitName(value: string | undefined): { firstName?: string; lastName?: string } {
  const parts = normalizeField(value)?.split(/\s+/).filter(Boolean) ?? []
  if (parts.length === 0) {
    return {}
  }

  const [firstName, ...lastNameParts] = parts
  return {
    firstName,
    lastName: lastNameParts.join(' ') || undefined,
  }
}

export function buildAirwallexBilling(
  payer: CheckoutPayerInput | undefined
): AirwallexBillingPayload | undefined {
  if (!payer) {
    return undefined
  }

  const name = splitName(payer.name)
  const street = [payer.billingAddress?.line1, payer.billingAddress?.line2]
    .map(normalizeField)
    .filter(Boolean)
    .join('\n')
  const address = {
    city: normalizeField(payer.billingAddress?.city),
    country_code: normalizeCountry(payer.billingAddress?.country),
    postcode: normalizeField(payer.billingAddress?.postalCode),
    state: normalizeField(payer.billingAddress?.state),
    street: street || undefined,
  }
  const normalized: AirwallexBillingPayload = {
    email: normalizeField(payer.email),
    first_name: name.firstName,
    last_name: name.lastName,
    phone_number: normalizeField(payer.phone),
    address: Object.values(address).some(Boolean) ? address : undefined,
  }

  return Object.values(normalized).some(Boolean) ? normalized : undefined
}

export function buildAirwallexCustomer(
  payer: CheckoutPayerInput | undefined
): Record<string, unknown> | undefined {
  const email = normalizeField(payer?.email)
  return email ? { email } : undefined
}
